import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../context/AuthContext';
import { FileText, Search, ArrowLeft, Calendar, User } from 'lucide-react';
import ShareButton from '../components/ShareButton';

export default function Articles() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredArticles, setFilteredArticles] = useState([]);

  useEffect(() => {
    loadArticles();
  }, []);

  useEffect(() => {
    if (id) {
      loadArticle(id);
    }
  }, [id]);

  useEffect(() => {
    filterArticles();
  }, [searchTerm, articles]);

  const loadArticles = async () => {
    try {
      const response = await axios.get(`${API}/articles`);
      setArticles(response.data);
      setFilteredArticles(response.data);
    } catch (error) {
      console.error('Failed to load articles:', error);
    }
  };

  const loadArticle = async (articleId) => {
    try {
      const response = await axios.get(`${API}/articles/${articleId}`);
      setSelectedArticle(response.data);
    } catch (error) {
      console.error('Failed to load article:', error);
    }
  };

  const filterArticles = () => {
    if (searchTerm) {
      const filtered = articles.filter(a =>
        a.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.content.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredArticles(filtered);
    } else {
      setFilteredArticles(articles);
    }
  };

  // Article Detail View
  if (id && selectedArticle) {
    return (
      <div className="max-w-4xl mx-auto space-y-6" data-testid="article-detail">
        <button
          onClick={() => navigate('/articles')}
          className="flex items-center space-x-2 text-purple-600 hover:text-purple-700"
          data-testid="back-to-articles-button"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Articles</span>
        </button>

        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {selectedArticle.image_url && (
            <div className="h-64 bg-gradient-to-br from-purple-400 to-blue-400">
              <img
                src={selectedArticle.image_url}
                alt={selectedArticle.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          <div className="p-8">
            <div className="flex items-center justify-between mb-4">
              <span className="px-4 py-2 bg-amber-100 text-amber-700 text-sm font-medium rounded-full">
                {selectedArticle.category}
              </span>
              <ShareButton
                title={selectedArticle.title}
                description={selectedArticle.summary}
                imageUrl={selectedArticle.image_url}
                contentType="article"
                contentId={selectedArticle.id}
                variant="button"
              />
            </div>

            <h1 className="text-4xl font-bold text-gray-800 mt-4 mb-4">{selectedArticle.title}</h1>

            <div className="flex items-center space-x-4 text-gray-600 mb-6">
              <div className="flex items-center space-x-2">
                <User className="w-4 h-4" />
                <span className="text-sm">{selectedArticle.author}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Calendar className="w-4 h-4" />
                <span className="text-sm">
                  {new Date(selectedArticle.created_at).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </span>
              </div>
            </div>

            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{selectedArticle.content}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Articles List View
  return (
    <div className="space-y-6" data-testid="articles-page">
      <div>
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Wellness Articles</h1>
        <p className="text-gray-600">Learn and grow on your wellness journey</p>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl p-4 shadow-lg border border-amber-100">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search articles..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            data-testid="search-input"
          />
        </div>
      </div>

      {/* Articles Grid */}
      {filteredArticles.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-amber-100">
          <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No articles found.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filteredArticles.map((article) => (
            <Link
              key={article.id}
              to={`/articles/${article.id}`}
              className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all border border-amber-100 hover:-translate-y-1"
              data-testid={`article-card-${article.id}`}
            >
              {article.image_url && (
                <div className="h-48 bg-gradient-to-br from-purple-400 to-blue-400 relative group">
                  <img
                    src={article.image_url}
                    alt={article.title}
                    className="w-full h-full object-cover"
                  />
                  <div 
                    className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={(e) => e.preventDefault()}
                  >
                    <ShareButton
                      title={article.title}
                      description={article.summary || article.content?.substring(0, 150)}
                      imageUrl={article.image_url}
                      contentType="article"
                      contentId={article.id}
                      variant="card"
                      className="!p-2 !bg-white/90 backdrop-blur-sm"
                    />
                  </div>
                </div>
              )}
              <div className="p-6">
                <span className="px-3 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                  {article.category}
                </span>
                <h3 className="text-xl font-semibold text-gray-800 mt-3 mb-2">{article.title}</h3>
                <p className="text-gray-600 text-sm line-clamp-3 mb-4">{article.content}</p>
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <div className="flex items-center space-x-1">
                    <User className="w-4 h-4" />
                    <span>{article.author}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Calendar className="w-4 h-4" />
                    <span>{new Date(article.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
